import { createApi } from '@reduxjs/toolkit/query/react';
import { customBaseQuery } from '@/lib/baseQuery';
import { ApiResponse } from '@/shared/types/base';
import {
  AtoTaxBracketResponse,
  CreateAtoTaxBracketDto,
  UpdateAtoTaxBracketDto,
  StateTaxRateResponse,
  CreateStateTaxRateDto,
  UpdateStateTaxRateDto,
  HemBenchmarkResponse,
  CreateHemBenchmarkDto,
  UpdateHemBenchmarkDto,
  CalculationSettingResponse,
  CreateCalculationSettingDto,
  UpdateCalculationSettingDto,
} from '@/pages/settings/types/rates-settings.types';

const ATO_BASE = '/v1/settings/ato-tax-brackets';
const STATE_BASE = '/v1/settings/state-tax-rates';
const HEM_BASE = '/v1/settings/hem-benchmarks';
const CALC_BASE = '/v1/settings/calculation-settings';
export const ratesSettingsApi = createApi({
  reducerPath: 'mortgageSettingsApi',
  baseQuery: customBaseQuery,
  tagTypes: ['AtoTaxBracket', 'StateTaxRate', 'HemBenchmark', 'CalculationSetting'],
  endpoints: (builder) => ({
    // ── ATO Tax Brackets ──
    getAtoTaxBrackets: builder.query<
      ApiResponse<AtoTaxBracketResponse[]>,
      void
    >({
      query: () => ATO_BASE,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({
                type: 'AtoTaxBracket' as const,
                id,
              })),
              { type: 'AtoTaxBracket', id: 'LIST' },
            ]
          : [{ type: 'AtoTaxBracket', id: 'LIST' }],
    }),

    createAtoTaxBracket: builder.mutation<
      ApiResponse<AtoTaxBracketResponse>,
      CreateAtoTaxBracketDto
    >({
      query: (body) => ({ url: ATO_BASE, method: 'POST', body }),
      invalidatesTags: [{ type: 'AtoTaxBracket', id: 'LIST' }],
    }),

    updateAtoTaxBracket: builder.mutation<
      ApiResponse<AtoTaxBracketResponse>,
      { id: string } & UpdateAtoTaxBracketDto
    >({
      query: ({ id, ...body }) => ({
        url: `${ATO_BASE}/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'AtoTaxBracket', id },
        { type: 'AtoTaxBracket', id: 'LIST' },
      ],
    }),

    // ── State Tax Rates ──
    getStateTaxRates: builder.query<
      ApiResponse<StateTaxRateResponse[]>,
      void
    >({
      query: () => STATE_BASE,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({
                type: 'StateTaxRate' as const,
                id,
              })),
              { type: 'StateTaxRate', id: 'LIST' },
            ]
          : [{ type: 'StateTaxRate', id: 'LIST' }],
    }),

    createStateTaxRate: builder.mutation<
      ApiResponse<StateTaxRateResponse>,
      CreateStateTaxRateDto
    >({
      query: (body) => ({ url: STATE_BASE, method: 'POST', body }),
      invalidatesTags: [{ type: 'StateTaxRate', id: 'LIST' }],
    }),

    updateStateTaxRate: builder.mutation<
      ApiResponse<StateTaxRateResponse>,
      { id: string } & UpdateStateTaxRateDto
    >({
      query: ({ id, ...body }) => ({
        url: `${STATE_BASE}/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'StateTaxRate', id },
        { type: 'StateTaxRate', id: 'LIST' },
      ],
    }),

    // ── HEM Benchmarks ──
    getHemBenchmarks: builder.query<
      ApiResponse<HemBenchmarkResponse[]>,
      void
    >({
      query: () => HEM_BASE,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({
                type: 'HemBenchmark' as const,
                id,
              })),
              { type: 'HemBenchmark', id: 'LIST' },
            ]
          : [{ type: 'HemBenchmark', id: 'LIST' }],
    }),

    createHemBenchmark: builder.mutation<
      ApiResponse<HemBenchmarkResponse>,
      CreateHemBenchmarkDto
    >({
      query: (body) => ({ url: HEM_BASE, method: 'POST', body }),
      invalidatesTags: [{ type: 'HemBenchmark', id: 'LIST' }],
    }),

    updateHemBenchmark: builder.mutation<
      ApiResponse<HemBenchmarkResponse>,
      { id: string } & UpdateHemBenchmarkDto
    >({
      query: ({ id, ...body }) => ({
        url: `${HEM_BASE}/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'HemBenchmark', id },
        { type: 'HemBenchmark', id: 'LIST' },
      ],
    }),

    // ── Calculation Settings ──
    getCalculationSettings: builder.query<
      ApiResponse<CalculationSettingResponse[]>,
      void
    >({
      query: () => CALC_BASE,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({
                type: 'CalculationSetting' as const,
                id,
              })),
              { type: 'CalculationSetting', id: 'LIST' },
            ]
          : [{ type: 'CalculationSetting', id: 'LIST' }],
    }),

    createCalculationSetting: builder.mutation<
      ApiResponse<CalculationSettingResponse>,
      CreateCalculationSettingDto
    >({
      query: (body) => ({ url: CALC_BASE, method: 'POST', body }),
      invalidatesTags: [{ type: 'CalculationSetting', id: 'LIST' }],
    }),

    updateCalculationSetting: builder.mutation<
      ApiResponse<CalculationSettingResponse>,
      { id: string } & UpdateCalculationSettingDto
    >({
      query: ({ id, ...body }) => ({
        url: `${CALC_BASE}/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'CalculationSetting', id },
        { type: 'CalculationSetting', id: 'LIST' },
      ],
    }),

  }),
});

export const {
  useGetAtoTaxBracketsQuery,
  useCreateAtoTaxBracketMutation,
  useUpdateAtoTaxBracketMutation,
  useGetStateTaxRatesQuery,
  useCreateStateTaxRateMutation,
  useUpdateStateTaxRateMutation,
  useGetHemBenchmarksQuery,
  useCreateHemBenchmarkMutation,
  useUpdateHemBenchmarkMutation,
  useGetCalculationSettingsQuery,
  useCreateCalculationSettingMutation,
  useUpdateCalculationSettingMutation,
} = ratesSettingsApi;
